import { useState } from 'react';
import { Plus, X, Settings2, GripVertical, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { useModifierGroups, useProductModifiers } from '@/hooks/useModifiers';
import { useCurrency } from '@/hooks/useCurrency';

interface ProductModifiersEditorProps {
  productId: string | null;
  disabled?: boolean;
}

export function ProductModifiersEditor({ productId, disabled = false }: ProductModifiersEditorProps) {
  const { formatCurrency } = useCurrency();
  const { modifierGroups, isLoading: groupsLoading } = useModifierGroups();
  const {
    productModifiers,
    isLoading: productModifiersLoading,
    addModifierToProduct,
    removeModifierFromProduct,
  } = useProductModifiers(productId);

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');

  // Get modifier group IDs already assigned to product
  const assignedGroupIds = new Set(productModifiers.map(pm => pm.modifier_group_id));

  // Filter available groups (not already assigned)
  const availableGroups = modifierGroups.filter(g => !assignedGroupIds.has(g.id));

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const handleAddGroup = async () => {
    if (!selectedGroupId || !productId) return;

    try {
      await addModifierToProduct.mutateAsync({ modifierGroupId: selectedGroupId });
      setSelectedGroupId('');
    } catch (error) {
      console.error('Failed to add modifier group:', error);
    }
  };

  const handleRemoveGroup = async (groupId: string) => {
    if (!productId) return;

    try {
      await removeModifierFromProduct.mutateAsync({ modifierGroupId: groupId });
    } catch (error) {
      console.error('Failed to remove modifier group:', error);
    }
  };

  const isLoading = groupsLoading || productModifiersLoading;

  if (!productId) {
    return (
      <div className="text-center py-4 text-sm text-muted-foreground">
        Save the product first to assign modifiers
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Add modifier group */}
      {availableGroups.length > 0 && !disabled && (
        <div className="flex gap-2">
          <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Select a modifier group to add..." />
            </SelectTrigger>
            <SelectContent>
              {availableGroups.map(group => (
                <SelectItem key={group.id} value={group.id}>
                  <div className="flex items-center gap-2">
                    <span>{group.name}</span>
                    {group.is_required && (
                      <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">
                        Required
                      </Badge>
                    )}
                    <span className="text-muted-foreground text-xs">
                      ({group.options?.length || 0} options)
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={handleAddGroup}
            disabled={!selectedGroupId || addModifierToProduct.isPending}
            size="icon"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Empty state */}
      {productModifiers.length === 0 && !isLoading && (
        <div className="text-center py-6 border-2 border-dashed rounded-lg">
          <Settings2 className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            No modifier groups assigned
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Add modifier groups to let customers customize this product
          </p>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent mx-auto" />
        </div>
      )}

      {/* Assigned modifier groups */}
      {productModifiers.length > 0 && (
        <div className="space-y-2">
          {productModifiers.map(pm => {
            const group = pm.modifier_group;
            if (!group) return null;

            return (
              <div
                key={pm.id}
                className="border rounded-lg bg-card overflow-hidden"
              >
                <Collapsible
                  open={expandedGroups.has(group.id)}
                  onOpenChange={() => toggleGroup(group.id)}
                >
                  <div className="flex items-center gap-2 p-3">
                    <GripVertical className="h-4 w-4 text-muted-foreground cursor-move" />
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0">
                        {expandedGroups.has(group.id) ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </Button>
                    </CollapsibleTrigger>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{group.name}</span>
                        {group.is_required && (
                          <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">
                            Required
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {group.is_required
                          ? `Must select ${group.min_selections ?? 1}${(group.max_selections ?? 1) > (group.min_selections ?? 1) ? `-${group.max_selections}` : ''}`
                          : `Up to ${group.max_selections ?? 1} selections`
                        }
                        {' '}&bull; {group.options?.length ?? 0} options
                      </p>
                    </div>
                    {!disabled && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => handleRemoveGroup(group.id)}
                        disabled={removeModifierFromProduct.isPending}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  <CollapsibleContent>
                    <div className="border-t bg-muted/30 p-3">
                      {group.options && group.options.length > 0 ? (
                        <div className="space-y-1">
                          {group.options.map(option => (
                            <div
                              key={option.id}
                              className="flex items-center justify-between text-sm py-1"
                            >
                              <div className="flex items-center gap-2">
                                <span className={cn(!option.is_active && 'text-muted-foreground line-through')}>
                                  {option.name}
                                </span>
                                {option.is_default && (
                                  <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                                    Default
                                  </Badge>
                                )}
                              </div>
                              <span className={cn(
                                "text-sm",
                                (option.price_adjustment ?? 0) > 0 && "text-amber-600",
                                (option.price_adjustment ?? 0) < 0 && "text-green-600"
                              )}>
                                {(option.price_adjustment ?? 0) === 0
                                  ? 'Free'
                                  : `${(option.price_adjustment ?? 0) > 0 ? '+' : ''}${formatCurrency(option.price_adjustment ?? 0)}`
                                }
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-2">
                          No options defined for this group
                        </p>
                      )}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            );
          })}
        </div>
      )}

      {/* Help text */}
      {productModifiers.length > 0 && modifierGroups.length > 0 && availableGroups.length === 0 && (
        <p className="text-xs text-muted-foreground text-center">
          All available modifier groups are assigned. Create more in Products &gt; Modifiers.
        </p>
      )}
    </div>
  );
}
